import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Divider,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    TextField,
    Typography
} from "@mui/material";
import { Delete, Edit, Link as LinkIcon, Visibility, VisibilityOff } from "@mui/icons-material";
import { useAuth } from "../../hooks/useAuth";
import noticeService from "../../services/noticeService";
import { PRIORITY_OPTIONS, htmlToPlainText, validateNoticeDraft } from "../../utils/noticeUtils";

const initialForm = {
    title: "",
    contentHtml: "",
    linkUrl: "",
    priority: "medium",
    startAt: "",
    expiryAt: "",
    isVisible: true
};

const priorityColors = {
    high: "error",
    medium: "warning",
    low: "info"
};

export default function AdminNoticeBoardPanel() {
    const { userProfile } = useAuth();
    const [form, setForm] = useState(initialForm);
    const [editingNoticeId, setEditingNoticeId] = useState("");
    const [notices, setNotices] = useState([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const editorRef = useRef(null);

    useEffect(() => {
        const unsub = noticeService.subscribeAllNotices(
            (data) => setNotices(data),
            (err) => setError(err?.message || "Failed to load notices")
        );
        return () => {
            if (typeof unsub === "function") unsub();
        };
    }, []);

    useEffect(() => {
        if (editorRef.current && editorRef.current.innerHTML !== form.contentHtml) {
            editorRef.current.innerHTML = form.contentHtml || "";
        }
    }, [form.contentHtml]);

    const validation = useMemo(() => validateNoticeDraft(form), [form]);

    const applyFormat = (command) => {
        if (editorRef.current) {
            editorRef.current.focus();
            document.execCommand(command, false);
            const html = editorRef.current.innerHTML;
            setForm((prev) => ({ ...prev, contentHtml: html }));
        }
    };

    const handleEditorInput = () => {
        const html = editorRef.current?.innerHTML || "";
        setForm((prev) => ({ ...prev, contentHtml: html }));
    };

    const resetForm = () => {
        setForm(initialForm);
        setEditingNoticeId("");
        setSuccess("");
        setError("");
        if (editorRef.current) {
            editorRef.current.innerHTML = "";
        }
    };

    const fillFormForEdit = (notice) => {
        setEditingNoticeId(notice.id);
        setForm({
            title: notice.title || "",
            contentHtml: notice.contentHtml || "",
            linkUrl: notice.linkUrl || "",
            priority: notice.priority || "medium",
            startAt: notice.startAt ? new Date(notice.startAt).toISOString().slice(0, 16) : "",
            expiryAt: notice.expiryAt ? new Date(notice.expiryAt).toISOString().slice(0, 16) : "",
            isVisible: notice.isVisible !== false
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!validation.isValid) {
            setError(Object.values(validation.errors)[0] || "Please fix form errors");
            return;
        }
        try {
            setSaving(true);
            setError("");
            setSuccess("");
            if (editingNoticeId) {
                await noticeService.updateNotice(editingNoticeId, form, userProfile?.uid);
                setSuccess("Notice updated successfully.");
            } else {
                await noticeService.createNotice(form, userProfile?.uid);
                setSuccess("Notice created successfully.");
            }
            resetForm();
        } catch (submitError) {
            setError(submitError?.message || "Failed to save notice");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (noticeId) => {
        try {
            await noticeService.deleteNotice(noticeId);
            setSuccess("Notice deleted.");
        } catch (deleteError) {
            setError(deleteError?.message || "Failed to delete notice");
        }
    };

    const toggleVisibility = async (notice) => {
        try {
            await noticeService.setNoticeVisibility(notice.id, !notice.isVisible);
        } catch (toggleError) {
            setError(toggleError?.message || "Failed to update visibility");
        }
    };

    return (
        <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#003366", mb: 2 }}>
                    Notice Board Management
                </Typography>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

                <Box component="form" onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Notice Title"
                                value={form.title}
                                onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                                error={!!validation.errors.title}
                                helperText={validation.errors.title || "Required"}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <InputLabel>Priority</InputLabel>
                                <Select
                                    label="Priority"
                                    value={form.priority}
                                    onChange={(e) => setForm((prev) => ({ ...prev, priority: e.target.value }))}
                                >
                                    {PRIORITY_OPTIONS.map((priority) => (
                                        <MenuItem key={priority} value={priority}>
                                            {priority.toUpperCase()}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="External Link URL"
                                value={form.linkUrl}
                                onChange={(e) => setForm((prev) => ({ ...prev, linkUrl: e.target.value }))}
                                error={!!validation.errors.linkUrl}
                                helperText={validation.errors.linkUrl || "Required"}
                                InputProps={{
                                    startAdornment: <LinkIcon sx={{ color: "text.secondary", mr: 1 }} />
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                type="datetime-local"
                                label="Start Date & Time"
                                InputLabelProps={{ shrink: true }}
                                value={form.startAt}
                                onChange={(e) => setForm((prev) => ({ ...prev, startAt: e.target.value }))}
                                error={!!validation.errors.startAt}
                                helperText={validation.errors.startAt || "Defaults to immediate publish"}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                type="datetime-local"
                                label="Expiry Date & Time"
                                InputLabelProps={{ shrink: true }}
                                value={form.expiryAt}
                                onChange={(e) => setForm((prev) => ({ ...prev, expiryAt: e.target.value }))}
                                error={!!validation.errors.expiryAt}
                                helperText={validation.errors.expiryAt || "Optional"}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                                <Button size="small" variant="outlined" onClick={() => applyFormat("bold")}>Bold</Button>
                                <Button size="small" variant="outlined" onClick={() => applyFormat("italic")}>Italic</Button>
                                <Button size="small" variant="outlined" onClick={() => applyFormat("underline")}>Underline</Button>
                                <Button size="small" variant="outlined" onClick={() => applyFormat("insertUnorderedList")}>Bullet</Button>
                            </Stack>
                            <Box
                                ref={editorRef}
                                contentEditable
                                suppressContentEditableWarning
                                aria-label="Notice Content Editor"
                                onInput={handleEditorInput}
                                sx={{
                                    minHeight: 140,
                                    p: 1.5,
                                    border: "1px solid",
                                    borderColor: validation.errors.content ? "error.main" : "divider",
                                    borderRadius: 1.5,
                                    outline: "none",
                                    "&:focus": { borderColor: "#003366" }
                                }}
                            />
                            <Typography variant="caption" color={validation.errors.content ? "error" : "text.secondary"}>
                                {validation.errors.content || `${htmlToPlainText(form.contentHtml).length} characters`}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <Typography variant="body2">Visible to students</Typography>
                                    <Switch
                                        checked={form.isVisible}
                                        onChange={(e) => setForm((prev) => ({ ...prev, isVisible: e.target.checked }))}
                                        inputProps={{ "aria-label": "toggle notice visibility" }}
                                    />
                                </Stack>
                                <Stack direction="row" spacing={1}>
                                    <Button variant="outlined" onClick={resetForm} disabled={saving}>Cancel</Button>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        disabled={saving || !validation.isValid}
                                        sx={{ backgroundColor: "#003366", "&:hover": { backgroundColor: "#02264a" } }}
                                    >
                                        {saving ? "Saving..." : editingNoticeId ? "Update Notice" : "Publish Notice"}
                                    </Button>
                                </Stack>
                            </Stack>
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Stack spacing={1.25}>
                    {notices.length === 0 ? (
                        <Alert severity="info">No notices published yet.</Alert>
                    ) : (
                        notices.slice(0, 8).map((notice) => (
                            <Box
                                key={notice.id}
                                sx={{
                                    p: 1.25,
                                    border: "1px solid #e5e7eb",
                                    borderLeft: "4px solid",
                                    borderLeftColor:
                                        notice.priority === "high"
                                            ? "#C1272D"
                                            : notice.priority === "medium"
                                                ? "#f59e0b"
                                                : "#0ea5e9",
                                    borderRadius: 1.5
                                }}
                            >
                                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
                                    <Box>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Typography sx={{ fontWeight: 700, color: "#003366" }}>{notice.title}</Typography>
                                            <Chip
                                                size="small"
                                                color={priorityColors[notice.priority] || "default"}
                                                label={String(notice.priority || "low").toUpperCase()}
                                            />
                                            <Chip
                                                size="small"
                                                icon={notice.isVisible ? <Visibility /> : <VisibilityOff />}
                                                label={notice.isVisible ? "Visible" : "Hidden"}
                                                variant="outlined"
                                            />
                                        </Stack>
                                        <Typography variant="body2" color="text.secondary">
                                            {notice.contentText}
                                        </Typography>
                                    </Box>
                                    <Stack direction="row" spacing={1}>
                                        <Button size="small" startIcon={<Edit />} onClick={() => fillFormForEdit(notice)}>Edit</Button>
                                        <Button size="small" onClick={() => toggleVisibility(notice)}>
                                            {notice.isVisible ? "Hide" : "Show"}
                                        </Button>
                                        <Button size="small" color="error" startIcon={<Delete />} onClick={() => handleDelete(notice.id)}>
                                            Delete
                                        </Button>
                                    </Stack>
                                </Stack>
                            </Box>
                        ))
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
}
